// NOTE: This is a general purpose utility file. For more complex utilities,
// create their own new util files in this same 'utils' directory.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';

dayjs.extend(utc);

/* SYNCHRONOUS */

// generates random unique React child keys
// WARNING: be careful when using inside helper fn/components with loops; React identifies every
// element instance with randomly generated keys as unique and may cause unnecessary re-renders,
// especially for inputs. DEFAULT TO USE FIXED KEYS WHENEVER POSSIBLE.
export const generateRandomKey = (prefix) =>
  prefix ? `${prefix}_${Math.random().toString(36).slice(2)}` : Math.random().toString(36).slice(2);

export const boolToNum = (bool) => (bool ? 1 : 0);
export const stringToBool = (str) => str === 'true';
export const boolToString = (bool) => (bool === true ? 'true' : 'false');

// cb fn to be used as PropType to omit a specific props coming from a shared type
export const omitSharedPropType = (props, propName, componentName) => {
  const propVal = props[propName];
  if (propVal === null || propVal === undefined) return;

  return new Error(`${componentName}'s ${propName} prop should be omitted`);
};

// formats a date to specific format.
export const formatDate = (rawDate, formatPattern = 'MM/DD/YYYY') =>
  dayjs(rawDate).format(formatPattern).toString();

// formats a given date in UTC.
export const formatDateUTC = (rawDate, formatPattern = 'MM/DD/YYYY') => {
  if (!rawDate) return null;

  return dayjs.utc(rawDate).format(formatPattern);
};

// converts string to Uppercase with under score
export const capitalizeWithUnderScore = (text) => text.toLocaleUpperCase().replace(/ /g, '_');

// transforms phrases/sentences/other cased text separated by ` `, `_` or `-` to be
// camel cased variables. all other symbols are parsed out from output.
// e.g. 'this-Is_a test String!' -> 'thisIsATestString'
// NOTE all caps abbreviations are treated as singular words
// e.g. 'BP trend' -> 'bpTrend'; 'My MAC Address' -> 'myMacAddress'
export const toCamelCase = (str) => {
  if (typeof str !== 'string') return '';

  const cleanedStr = str.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
  const words = cleanedStr.split(' ');

  const processedWords = words.map((word, index) => {
    if (word.toUpperCase() === word && word.length > 1) {
      return index === 0 ? word.toLowerCase() : word[0] + word.slice(1).toLowerCase();
    }
    return index === 0
      ? word.toLowerCase()
      : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return processedWords.join('');
};

export const toPascalCase = (str) => {
  if (typeof str === 'string') {
    return str.replace(/(\w)(\w*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase());
  }
  return str;
};

// joins two array of objects together, ordered by an array of strings (labelOrder) matching by
// specified key value (matchKey)
export const mergeAndOrderArrays = (arrayOne, arrayTwo, labelOrder, matchKey) =>
  [...arrayOne, ...arrayTwo].sort((a, b) => {
    const labelAIndex = labelOrder.indexOf(a?.[matchKey]);
    const labelBIndex = labelOrder.indexOf(b?.[matchKey]);

    if (labelAIndex === -1) return 1;
    if (labelBIndex === -1) return -1;

    return labelAIndex - labelBIndex;
  });

// checks for valid url links using regex
export const validateLink = (value) =>
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/.test(
    value,
  ) ||
  /^[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&//=]*)$/.test(
    value,
  );

export const blankDropdownOption = { optionLabel: '' };

// parses an array of objects with duplicate entries and returns array with only unique items
// NOTE potentially heavy operation, use sparingly
// time complexity: O(n * k)
// n = number of entries
// k = average number of properties within each entry
export const getUniqueEntries = (arr) => {
  const uniqueEntries = new Map();

  arr.forEach((obj) => {
    const key = Object.entries(obj).toString();
    uniqueEntries.set(key, obj);
  });

  return Array.from(uniqueEntries.values());
};

/* ASYNCHRONOUS */
// executes a single network request
export const makeRequest = async (
  method,
  payload,
  shouldReturnResolvedData = false,
  onErrorCallbacks = [],
) => {
  try {
    const responseData = await method(payload).unwrap();
    return shouldReturnResolvedData ? responseData : Promise.resolve();
  } catch (err) {
    console.error(err);
    if (onErrorCallbacks.length) {
      onErrorCallbacks.forEach((cb) => cb());
    }
    return Promise.reject(err);
  }
};

// executes a queue of multiple network requests with the same method
export const makeQueuedRequests = async (
  method,
  requests = [],
  shouldReturnResolvedData = false,
  onErrorCallbacks = [],
) => {
  try {
    const requestArray = Array.isArray(requests) ? requests : [requests];
    const responseData = await Promise.all(requestArray.map((request) => method(request).unwrap()));

    return shouldReturnResolvedData ? responseData : Promise.resolve();
  } catch (err) {
    console.error(err);
    if (onErrorCallbacks.length) {
      onErrorCallbacks.forEach((cb) => cb());
    }
    return Promise.reject(err);
  }
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const convertActiveStatusToYesNoUnknown = (status) => {
  if (status?.toLowerCase() === 'active') return 'Yes';
  if (status?.toLowerCase() === 'inactive') return 'No';
  return 'Unknown';
};

export const convertInchesToFeet = (heightInInches) => {
  if (typeof heightInInches !== 'number' || heightInInches < 0) {
    throw new Error('Input must be a non-negative number');
  }
  if (heightInInches === 0) return 'Not reported';
  const feet = Math.floor(heightInInches / 12); // Get the whole number of feet
  const remainingInches = heightInInches % 12; // Get the remaining inches
  return `${feet}' ${remainingInches}"`;
};

export const objectIsEmpty = (obj) => {
  if (!obj || typeof obj !== 'object') return true;
  return Object.keys(obj).length === 0;
};

export const getDateWithAbbreviation = (date = new Date(), format = 'MM/DD/YYYY [at] h:mmA') => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  const timezone = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';

  return `${dayjs(date).format(format)} ${timezone}`;
};

export const getMemberNames = (name) => {
  const nameData = name.find((item) => item.use === 'official') || name[0];

  const firstName = nameData.given?.[0] ?? '';
  const lastName = nameData.family ?? '';

  return {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
  };
};

export const safeRound = (value) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return null;
  }
  return Math.round(value);
};

export const formatWithCommas = (value) => {
  const roundedValue = safeRound(value);
  if (roundedValue === null) {
    return null;
  }
  return roundedValue.toLocaleString();
};

export const svgToDataUri = (svg) => {
  // Remove line breaks and extra spaces
  const cleaned = svg.replace(/\n/g, '').replace(/\s{2,}/g, ' ');
  // Encode special characters
  const encoded = encodeURIComponent(cleaned).replace(/'/g, '%27').replace(/"/g, '%22');
  return `data:image/svg+xml,${encoded}`;
};

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const alphanumericRegex = /^[a-zA-Z0-9]+$/;

export const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  switch (true) {
    case digits.length > 6:
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    case digits.length > 3:
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    case digits.length > 0:
      return `(${digits}`;
    default:
      return '';
  }
};

export const formatDateInput = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  switch (true) {
    case digits.length > 4:
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    case digits.length > 2:
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    case digits.length > 0:
      return digits;
    default:
      return '';
  }
};

export const cleanPresignedUrl = (url) => {
  // Replace all HTML-encoded ampersands with actual &
  return (url || '').replace(/&amp;/g, '&');
};

/**
 * Validates and sanitizes presigned URLs to prevent SSRF attacks (CWE-918)
 *
 * Security Controls:
 * - Validates URL is not empty/null
 * - Enforces HTTPS protocol only (prevents http://, file://, etc.)
 * - Validates hostname against allowlist (amazonaws.com)
 * - Prevents access to internal resources (localhost, 127.0.0.1, 169.254.x.x, etc.)
 *
 * @param {string} url - The presigned URL to validate
 * @returns {string} The validated URL
 * @throws {Error} If URL is invalid, not HTTPS, or host is not allowed
 */
export const sanitizePresignedUrl = (url) => {
  const cleaned = cleanPresignedUrl(url);
  const ALLOWED_PRESIGNED_HOST = 'amazonaws.com';
  if (!cleaned) {
    throw new Error('Missing presigned URL');
  }

  let parsed;
  try {
    parsed = new URL(cleaned);
  } catch (err) {
    console.error('Error parsing presigned URL:', err);
    throw new Error('Invalid presigned URL');
  }

  if (parsed.protocol !== 'https:') {
    throw new Error('Presigned URL must use https');
  }

  const suffixes = [ALLOWED_PRESIGNED_HOST];
  const isAllowedHost = suffixes.some(
    (suffix) => parsed.hostname === suffix || parsed.hostname.endsWith(`.${suffix}`),
  );

  if (!isAllowedHost) {
    throw new Error('Presigned URL host is not allowed');
  }

  return parsed.toString();
};
